/**
 * Automation accounts feature — queries + mutations.
 *
 * Backed by internal/admin/serviceaccount.Handler, mounted at
 *   Platform scope:  /api/v1/automation-accounts
 *   Tenant scope:    /api/v1/tenants/{tenant_id}/automation-accounts
 *
 * Step-up enforcement mirrors the Platform admins surface —
 * create / rotate / update / delete go through withStepUp; reads
 * do not. The raw token returned on create/rotate is NEVER
 * written to the query cache: the mutation's resolved promise
 * is the one-time carrier, and list invalidation refetches the
 * metadata-only shape afterwards.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import { queryKeys } from "@/lib/query-keys";
import {
  createAutomationAccountSchema,
  rotateAutomationAccountSchema,
  automationAccountListSchema,
  automationAccountSchema,
  updateAutomationAccountSchema,
  type CreateAutomationAccountInput,
  type RotateAutomationAccountInput,
  type AutomationAccount,
  type AutomationAccountList,
  type UpdateAutomationAccountInput,
} from "./schemas";

/**
 * Build the REST base for the requested scope. Platform scope
 * passes `null` tenantID. Tenant scope passes the tenant's UUID.
 * Kept as a single helper so call sites can't diverge on URL
 * shape — the two surfaces use identical verbs and parameters.
 */
function base(tenantID: string | null): string {
  if (tenantID === null) {
    return "/api/v1/automation-accounts";
  }
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/automation-accounts`;
}

/** Map a null/UUID scope to the registry's scope-string convention. */
export function scopeKey(tenantID: string | null): string {
  return tenantID === null ? "platform" : tenantID;
}

// ── Queries ──────────────────────────────────────────────────────────

/** List options. `showInactive=false` asks the backend for
 *  active-only rows; omitting/true returns all statuses. */
export interface FetchAutomationAccountsOpts {
  showInactive?: boolean;
}

export async function fetchAutomationAccounts(
  tenantID: string | null,
  opts: FetchAutomationAccountsOpts = {},
): Promise<AutomationAccountList> {
  const raw = await apiFetch<unknown>(base(tenantID), {
    searchParams: opts.showInactive ? undefined : { status: "active" },
  });
  return automationAccountListSchema.parse(raw);
}

export function useAutomationAccountsQuery(
  tenantID: string | null,
  opts: FetchAutomationAccountsOpts = {},
) {
  // Filter scope lives in the query key so "show inactive" and
  // "active only" are separate cache entries — toggling the
  // URL switch refetches cleanly and doesn't reuse the wrong
  // pre-filtered list.
  const filter = { showInactive: !!opts.showInactive } as const;
  return useQuery<AutomationAccountList, ApiError>({
    queryKey: [
      ...queryKeys.automationAccounts.list(scopeKey(tenantID)),
      filter,
    ] as const,
    queryFn: () => fetchAutomationAccounts(tenantID, opts),
    // Rotation / creation mutations invalidate; keep stale-time
    // modest so manual visits get a fresh view.
    staleTime: 30_000,
  });
}

export async function fetchAutomationAccount(
  tenantID: string | null,
  id: string,
): Promise<AutomationAccount> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(id)}`,
  );
  return automationAccountSchema.parse(raw);
}

export function useAutomationAccountQuery(
  tenantID: string | null,
  id: string | null,
) {
  return useQuery<AutomationAccount, ApiError>({
    // Empty-id fallback key keeps the query disabled without TS
    // fighting the `readonly unknown[]` constraint. The `enabled`
    // guard below is what actually prevents the fetch.
    queryKey: queryKeys.automationAccounts.detail(scopeKey(tenantID), id ?? ""),
    queryFn: () => fetchAutomationAccount(tenantID, id as string),
    enabled: !!id,
    staleTime: 30_000,
  });
}

// ── Mutations ────────────────────────────────────────────────────────

export function useCreateAutomationAccountMutation(tenantID: string | null) {
  const qc = useQueryClient();
  return useMutation<AutomationAccount, ApiError, CreateAutomationAccountInput>({
    mutationFn: (input) => {
      // Validate client-side before hitting the network — Zod
      // .parse throws on malformed input so a malformed
      // callsite surfaces synchronously instead of round-
      // tripping to the backend.
      const validated = createAutomationAccountSchema.parse(input);
      return withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: validated,
        }).then((raw) => automationAccountSchema.parse(raw)),
      );
    },
    onSuccess: () => {
      // Invalidate the list so the new row appears. The raw
      // token in the resolved promise is the one-time carrier
      // the caller must capture — we deliberately do NOT write
      // it into the cache.
      qc.invalidateQueries({
        queryKey: queryKeys.automationAccounts.all(scopeKey(tenantID)),
      });
    },
  });
}

export function useRotateAutomationAccountMutation(tenantID: string | null) {
  const qc = useQueryClient();
  return useMutation<
    AutomationAccount,
    ApiError,
    { id: string; input: RotateAutomationAccountInput }
  >({
    mutationFn: ({ id, input }) => {
      const validated = rotateAutomationAccountSchema.parse(input);
      return withStepUp(() =>
        apiFetch<unknown>(
          `${base(tenantID)}/${encodeURIComponent(id)}/rotate`,
          { method: "POST", body: validated },
        ).then((raw) => automationAccountSchema.parse(raw)),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.automationAccounts.all(scopeKey(tenantID)),
      });
    },
  });
}

export function useUpdateAutomationAccountMutation(
  tenantID: string | null,
  id: string,
) {
  const qc = useQueryClient();
  return useMutation<AutomationAccount, ApiError, UpdateAutomationAccountInput>({
    mutationFn: (input) => {
      // Zod validates the payload AND enforces "at least one field
      // must be set" via refine — so a no-op submit surfaces as a
      // client-side error instead of hitting the network.
      const validated = updateAutomationAccountSchema.parse(input);
      return withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: validated,
        }).then((raw) => automationAccountSchema.parse(raw)),
      );
    },
    onSuccess: (updated) => {
      // Prime the detail cache with the fresh row so the page
      // updates before the list roundtrips, and invalidate the
      // list so the row reflects the change in its summary.
      qc.setQueryData(
        queryKeys.automationAccounts.detail(scopeKey(tenantID), id),
        updated,
      );
      qc.invalidateQueries({
        queryKey: queryKeys.automationAccounts.list(scopeKey(tenantID)),
      });
    },
  });
}

/**
 * useSetAutomationAccountOwnerMutation assigns (or clears, with null) the accountable owner:
 * PUT .../automation-accounts/{id}/owner. Step-up-gated server-side
 * (principals.owner_reassign, the same act the agent registry's reassign carries), so it runs
 * through withStepUp. Clearing is legal: the inventory renders the unowned row as its
 * "no owner recorded" finding, which is the honest state — never a fabricated name.
 */
export function useSetAutomationAccountOwnerMutation(tenantID: string | null) {
  const qc = useQueryClient();
  return useMutation<AutomationAccount, ApiError, { id: string; ownerAdminID: string | null }>({
    mutationFn: ({ id, ownerAdminID }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(id)}/owner`, {
          method: "PUT",
          body: { owner_admin_id: ownerAdminID },
        }).then((raw) => automationAccountSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.automationAccounts.detail(scopeKey(tenantID), updated.id), updated);
      qc.invalidateQueries({
        queryKey: queryKeys.automationAccounts.all(scopeKey(tenantID)),
      });
    },
  });
}

export function useDeleteAutomationAccountMutation(tenantID: string | null) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(id)}`, {
          method: "DELETE",
        }),
      ),
    onSuccess: (_res, id) => {
      // Drop the detail cache for the now-gone SA so navigating
      // back to its URL shows the empty state rather than a stale
      // row. The list invalidation covers the grid.
      qc.removeQueries({
        queryKey: queryKeys.automationAccounts.detail(scopeKey(tenantID), id),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.automationAccounts.list(scopeKey(tenantID)),
      });
    },
  });
}
